import { memo, useMemo } from 'react'
import type { ICellRendererParams } from 'ag-grid-community'

import { AdaptedProduct } from '@/types'
import { imgPath } from '@/services/adapters/pictures.adapter'
import { usePreloadImage } from '@/hooks/usePreloadImage'

const DEFAULT_IMAGE = `${process.env.NEXT_PUBLIC_ASSETS_PATH}/thumbnail/default.png`
const DEFAULT_PLACEHOLDER_PICTURE = 'default.png'

const isDefaultPlaceholderPicture = (picture: string) => {
  const sanitizedPicture = picture.split('?')[0]?.split('#')[0] ?? picture

  return (
    sanitizedPicture === DEFAULT_PLACEHOLDER_PICTURE ||
    sanitizedPicture.endsWith(`/${DEFAULT_PLACEHOLDER_PICTURE}`)
  )
}

type LoadingImageProps = {
  alt: string
  className: string
  picture: string
}

const isSvgPicture = (picture: string) => {
  const sanitizedPicture = picture.split('?')[0]?.split('#')[0] ?? picture

  return sanitizedPicture.toLowerCase().endsWith('.svg')
}

const LoadingImage = memo(({ picture, alt, className }: LoadingImageProps) => {
  const target = picture ? imgPath(picture, 'thumbnail') : DEFAULT_IMAGE
  const fallbackTarget =
    picture && !isDefaultPlaceholderPicture(picture)
      ? imgPath(picture, 'img')
      : undefined
  const loadedSrc = usePreloadImage(
    target,
    DEFAULT_IMAGE,
    3,
    500,
    fallbackTarget,
  )

  return <img src={loadedSrc} alt={alt} className={className} />
})

LoadingImage.displayName = 'LoadingImage'

type PictureThumbnailProps = {
  alt: string
  picture: string
}

const PictureThumbnail = memo(({ picture, alt }: PictureThumbnailProps) => {
  if (isSvgPicture(picture)) {
    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-white">
        <LoadingImage
          picture={picture}
          alt={alt}
          className="h-1/2 w-1/2 object-contain"
        />
      </div>
    )
  }

  return (
    <LoadingImage
      picture={picture}
      alt={alt}
      className="h-10 w-10 rounded-sm object-cover"
    />
  )
})

PictureThumbnail.displayName = 'PictureThumbnail'

export const ImageCellRenderer = memo(
  ({ data, value }: ICellRendererParams<AdaptedProduct>) => {
    const pictures = useMemo(() => {
      const rawPictures = Array.isArray(value)
        ? value.filter(
            (picture): picture is string =>
              typeof picture === 'string' && picture.length > 0,
          )
        : typeof value === 'string' && value.length > 0
          ? [value]
          : []

      return rawPictures.length > 0
        ? rawPictures
        : [DEFAULT_PLACEHOLDER_PICTURE]
    }, [value])
    const visiblePictures = useMemo(() => pictures.slice(0, 3), [pictures])

    if (data?.id?.includes('new')) return null

    const pictureKey = (picture: string, index: number) => {
      if (isDefaultPlaceholderPicture(picture)) return `default-${index}`
      return picture
    }

    return (
      <div className="flex gap-2">
        {visiblePictures.map((picture: string, i: number) => (
          <PictureThumbnail
            key={pictureKey(picture, i)}
            picture={picture}
            alt={data?.name?.label ?? 'default'}
          />
        ))}
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary rerenders
    const prevPictures = Array.isArray(prevProps.value)
      ? prevProps.value.slice(0, 3)
      : prevProps.value || []
    const nextPictures = Array.isArray(nextProps.value)
      ? nextProps.value.slice(0, 3)
      : nextProps.value || []
    return (
      prevProps.data?.id === nextProps.data?.id &&
      JSON.stringify(prevPictures) === JSON.stringify(nextPictures)
    )
  },
)

ImageCellRenderer.displayName = 'ImageCellRenderer'
